import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./ProfessionalCustomers.module.css";
import AddProfessionalCustomerModal from "../../../../components/Customers/AddProfessionalCustomerModal";
import {
  useProfessionalCustomersList,
  useCreateProfessionalCustomer,
  usePeopleFilters,
} from "../../../../api/people/usePeopleApi";

// ── Map API to UI format ────────────────────────────────────────────────────
const mapApiToUi = (c) => {
  const firstName = c.primaryContactFirstName ?? "";
  const lastName = c.primaryContactLastName ?? "";
  const name = [firstName, lastName].filter(Boolean).join(" ") || "—";
  return {
    id: c.id,
    ref: c.customerCode || `CUST-PRO-${String(c.id).padStart(5, "0")}`,
    createdAt: c.createdAt
      ? new Date(c.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "",
    companyName: c.tradingName ?? "",
    legalName: c.legalName ?? "",
    vatNumber: c.vatNumber ?? "",
    industry: c.industry ?? "",
    country: c.country ?? "",
    city: c.city ?? "",
    bookingsCount: c.totalBookings ?? 0,
    totalSpent: parseFloat(c.totalSpent ?? 0),
    openBalance: parseFloat(c.openBalance ?? 0),
    primaryContact: {
      name,
      email: c.primaryContactEmail ?? "",
      phone: c.primaryContactPhone ?? "",
    },
    status: c.status === "ACTIVE" ? "Active" : "Inactive",
  };
};

// ── Constants ──────────────────────────────────────────────────────────────
const STATUS_TABS = [
  { key: "All",      label: "All Companies" },
  { key: "Active",   label: "Active" },
  { key: "Inactive", label: "Inactive" },
];

// ── Helpers ────────────────────────────────────────────────────────────────
const companyInitials = (name) =>
  name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();

const avatarColor = (id) => {
  const colors = ["#2563eb","#059669","#9333ea","#d97706","#dc2626","#0891b2","#f5a51e","#16a34a"];
  return colors[id % colors.length];
};

// ── Component ──────────────────────────────────────────────────────────────
const ProfessionalCustomers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab,     setActiveTab]     = useState("All");
  const [searchTerm,    setSearchTerm]    = useState("");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  const { data: filtersData } = usePeopleFilters();
  const industries = useMemo(() => filtersData?.industry ?? [], [filtersData?.industry]);

  const { data: apiData, pagination, loading, error, refetch } = useProfessionalCustomersList({
    q: searchTerm || undefined,
    status: activeTab,
    balance: balanceFilter,
    industry: industryFilter !== "all" ? industryFilter : undefined,
    page,
    limit,
  });
  const { execute: createCompany, loading: saving, error: createError, clearError } = useCreateProfessionalCustomer();

  const companies = useMemo(() => (apiData ?? []).map(mapApiToUi), [apiData]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, balanceFilter, industryFilter, activeTab]);

  const tabCounts = useMemo(() => {
    const total = pagination?.total ?? 0;
    return { All: "—", Active: "—", Inactive: "—", [activeTab]: total };
  }, [pagination?.total, activeTab]);

  const hasActiveFilters = searchTerm !== "" || balanceFilter !== "all" || industryFilter !== "all";

  const pageNumbers = useMemo(() => {
    const total = pagination?.totalPages ?? 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const current = page;
    const pages = new Set([1, total]);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [pagination?.totalPages, page]);

  const handleOpenCompany = (c) =>
    navigate(`/people/customers/professional/${c.ref}`, { state: { company: c } });
  const handleOpenBookings = (c) =>
    navigate(`/people/customers/professional/${c.ref}`, { state: { company: c, defaultTab: "bookings" } });

  const handleAddClick = () => { setEditingCompany(null); clearError?.(); setModalOpen(true); };

  // Auto-open the "Add Company" form when navigated here from the booking
  // "Change Customer" flow (Add New Client button, Companies search).
  useEffect(() => {
    if (location.state?.openAdd) {
      setEditingCompany(null);
      clearError?.();
      setModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openAdd]);
  const handleSave = useCallback(
    async (payload) => {
      try {
        await createCompany(payload);
        setModalOpen(false);
        refetch();
      } catch (err) {
        console.error("Save company failed:", err);
      }
    },
    [createCompany, refetch]
  );

  const handleClear = () => {
    setSearchTerm("");
    setBalanceFilter("all");
    setIndustryFilter("all");
    setPage(1);
  };

  return (
    <div className={styles.container}>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>🏢</div>
          <div className={styles.headerText}>
            <h1>Professional Customers</h1>
            <p>Companies, agencies and organisations — legal and financial counterparty for all bookings</p>
          </div>
        </div>
        <button className={styles.addBtn} onClick={handleAddClick}>+ Add Company</button>
      </div>

      {/* ── Status Tabs ── */}
      <div className={styles.tabBar}>
        {STATUS_TABS.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              className={`${styles.tab} ${isActive ? styles.tabActive : ""} ${
                isActive && tab.key === "Active"   ? styles.tabActiveGreen : ""} ${
                isActive && tab.key === "Inactive" ? styles.tabActiveGray  : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className={`${styles.tabBadge} ${isActive ? styles.tabBadgeActive : ""} ${
                !isActive && tab.key === "Active"   ? styles.tabBadgeGreen : ""} ${
                !isActive && tab.key === "Inactive" ? styles.tabBadgeGray  : ""}`}>
                {tabCounts[tab.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Filter Bar ── */}
      <div className={styles.filterBar}>
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by ref, company, VAT or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.filterDivider} />
        <div className={styles.filterGroup}>
          <label>Balance:</label>
          <select value={balanceFilter} onChange={e => setBalanceFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="owed">Open Balance</option>
            <option value="clear">Fully Paid</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label>Industry:</label>
          <select value={industryFilter} onChange={e => setIndustryFilter(e.target.value)}>
            <option value="all">All</option>
            {industries.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>
        {hasActiveFilters && (
          <button className={styles.clearFilters} onClick={handleClear}>Clear Filters</button>
        )}
      </div>

      {/* ── Results Summary ── */}
      <div className={styles.resultsSummary}>
        {loading ? (
          "Loading..."
        ) : (
          <>
            Showing {companies.length} of {pagination?.total ?? 0} companies
            {hasActiveFilters && <span className={styles.filtered}>(filtered)</span>}
          </>
        )}
      </div>

      {/* ── Table ── */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Company</th>
            <th>Primary Contact</th>
            <th>Industry</th>
            <th>VAT Number</th>
            <th>Bookings</th>
            <th>Total Spent</th>
            <th>Open Balance</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="9">
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>⏳</div>
                  <p className={styles.emptyTitle}>Loading companies...</p>
                </div>
              </td>
            </tr>
          ) : companies.length > 0 ? companies.map(c => (
            <tr key={c.id}>

              <td>
                <div className={styles.companyCell}>
                  <div className={styles.avatar} style={{ background: avatarColor(c.id) }}>
                    {companyInitials(c.companyName)}
                  </div>
                  <div>
                    <div className={styles.companyName}>{c.companyName}</div>
                    <div className={styles.companyRef}>{c.ref}</div>
                    <div className={styles.companyLocation}>📍 {c.city}, {c.country}</div>
                  </div>
                </div>
              </td>

              <td>
                <div className={styles.contactName}>{c.primaryContact.name}</div>
                <div className={styles.contactEmail}>{c.primaryContact.email}</div>
                <div className={styles.contactPhone}>{c.primaryContact.phone}</div>
              </td>

              <td>
                <span className={styles.industryBadge}>{c.industry}</span>
              </td>

              <td>
                <span className={styles.vatNumber}>{c.vatNumber}</span>
              </td>

              <td>
                {c.bookingsCount > 0
                  ? <span className={styles.bookingCount}>{c.bookingsCount} booking{c.bookingsCount !== 1 ? "s" : ""}</span>
                  : <span className={styles.noBookings}>No bookings</span>}
              </td>

              <td>
                <div className={styles.price}>
                  {c.totalSpent > 0
                    ? `€${c.totalSpent.toLocaleString("en-EU", { minimumFractionDigits: 2 })}`
                    : "—"}
                </div>
              </td>

              <td>
                {c.openBalance > 0
                  ? <span className={styles.balanceOwed}>€{c.openBalance.toLocaleString("en-EU", { minimumFractionDigits: 2 })}</span>
                  : <span className={styles.balanceClear}>✓ Clear</span>}
              </td>

              <td>
                <span className={`${styles.statusBadge} ${c.status === "Active" ? styles.statusActive : styles.statusInactive}`}>
                  <span className={styles.statusDot} />
                  {c.status}
                </span>
              </td>

              <td>
                <div className={styles.rowActions}>
                  <button className={styles.btnOpen}     onClick={() => handleOpenCompany(c)}>Open</button>
                  <button className={styles.btnBookings} onClick={() => handleOpenBookings(c)}>Bookings</button>
                </div>
              </td>

            </tr>
          )) : (
            <tr>
              <td colSpan="9">
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>🏢</div>
                  <p className={styles.emptyTitle}>No companies found</p>
                  <p className={styles.emptySubtitle}>Try adjusting your filters or search term</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {(pagination?.totalPages ?? 1) > 1 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Page {pagination?.page ?? page} of {pagination?.totalPages ?? 1} ({pagination?.total ?? 0} total)
          </span>
          <div className={styles.pageNumbers}>
            {pageNumbers.map((p, idx) =>
              p === "…" ? (
                <span key={`ellipsis-${idx}`} className={styles.ellipsis}>…</span>
              ) : (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${Number(p) === Number(page) ? styles.pageBtnActive : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
          </div>
        </div>
      )}

      <AddProfessionalCustomerModal
        open={modalOpen}
        onClose={() => { clearError?.(); setModalOpen(false); }}
        onSave={handleSave}
        company={editingCompany}
        apiError={createError}
      />

    </div>
  );
};

export default ProfessionalCustomers;